import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import PhygitalIcon from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Scissors,
  Palette,
  Sparkles,
  Eye,
  Plus,
  Instagram,
  Calendar,
  Globe,
  DollarSign,
  Crown,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service categories
const SERVICE_CATEGORIES = [
  { id: "hair", name: "Hair", icon: Scissors, color: "bg-purple-500", desc: "Braids, cuts, color, styling" },
  { id: "makeup", name: "Makeup", icon: Palette, color: "bg-pink-500", desc: "Glam, bridal, editorial" },
  { id: "nails", name: "Nails", icon: Sparkles, color: "bg-rose-500", desc: "Manicures, acrylics, gel" },
  { id: "lashes", name: "Lashes", icon: Eye, color: "bg-amber-500", desc: "Extensions, lifts, tints" },
  { id: "other", name: "Other", icon: Plus, color: "bg-gray-500", desc: "Specify your service" }
];

// Pricing tiers
const PRICING_TIERS = [
  { 
    id: "solo", 
    name: "Solo", 
    price: "$11/mo", 
    desc: "Perfect for individual providers",
    features: ["Unlimited clients", "Custom branding", "Check-in system", "Late fee policy", "Pre-booking discounts"],
    popular: true
  },
  { 
    id: "team", 
    name: "Team", 
    price: "$44/mo", 
    desc: "For small salons",
    features: ["Everything in Solo", "Up to 5 team members", "Team analytics", "Shared calendar"],
    popular: false
  },
  { 
    id: "luxe", 
    name: "phygitalLuxe", 
    price: "$99/mo", 
    desc: "Full-service venues",
    features: ["Everything in Team", "Analytics dashboard", "Income projections", "NFC check-in reader", "Custom domain"],
    popular: false
  }
];

// Booking platforms
const BOOKING_PLATFORMS = [
  { id: "instagram", name: "Instagram DM", icon: Instagram },
  { id: "acuity", name: "Acuity", icon: Calendar },
  { id: "styleseat", name: "StyleSeat", icon: Globe },
  { id: "square", name: "Square", icon: Calendar },
  { id: "other", name: "Other", icon: Globe }
];

const ProviderSetupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [otherCategory, setOtherCategory] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [instagram, setInstagram] = useState("");
  const [bookingPlatform, setBookingPlatform] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [selectedTier, setSelectedTier] = useState("solo");

  const toggleCategory = (catId) => {
    setSelectedCategories(prev => 
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const handleSubmit = async () => {
    if (selectedCategories.length === 0) {
      toast.error("Please select at least one service category");
      return;
    }
    if (!businessName.trim()) {
      toast.error("Please enter your business name");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/provider/register`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          business_name: businessName,
          service_types: selectedCategories,
          other_service_type: otherCategory,
          instagram,
          booking_platform: bookingPlatform,
          booking_url: bookingUrl,
          plan: selectedTier
        })
      });

      if (res.ok) {
        toast.success("You're now a provider!");
        navigate("/provider");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to register as provider");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
          </Link>
          <div className="w-20" /> {/* Spacer */}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div 
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                s === step ? 'w-8 bg-purple-500' : s < step ? 'bg-purple-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Select Categories */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">What services do you provide?</h1>
              <p className="text-gray-600">Select all that apply</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SERVICE_CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedCategories.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center mb-3`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <p className="font-medium text-gray-900">{cat.name}</p>
                    <p className="text-xs text-gray-600">{cat.desc}</p>
                    {isSelected && (
                      <div className="mt-2">
                        <Check className="w-5 h-5 text-purple-500" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {selectedCategories.includes("other") && (
              <Input
                placeholder="Describe your service..."
                value={otherCategory}
                onChange={(e) => setOtherCategory(e.target.value)}
                className="mt-4"
              />
            )}

            <Button 
              onClick={() => setStep(2)} 
              disabled={selectedCategories.length === 0}
              className="w-full bg-purple-600 hover:bg-purple-700"
              size="lg"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Business Info */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Tell us about your business</h1>
              <p className="text-gray-600">This helps clients find you</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                <Input
                  placeholder="e.g., Glamour by Sarah"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram</label>
                <div className="relative">
                  <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="@yourusername"
                    value={instagram}
                    onChange={(e) => setInstagram(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Where do clients book you?</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {BOOKING_PLATFORMS.map((platform) => {
                    const Icon = platform.icon;
                    return (
                      <button
                        key={platform.id}
                        onClick={() => setBookingPlatform(platform.id)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          bookingPlatform === platform.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-5 h-5 mx-auto mb-1 text-gray-700" />
                        <p className="text-xs">{platform.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {bookingPlatform && bookingPlatform !== "instagram" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Booking URL</label>
                  <Input
                    placeholder="https://..."
                    value={bookingUrl}
                    onChange={(e) => setBookingUrl(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!businessName.trim()}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Select Plan */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose your plan</h1>
              <p className="text-gray-600">Start with a 14-day free trial</p>
            </div>

            <div className="space-y-3">
              {PRICING_TIERS.map((tier) => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all relative ${
                    selectedTier === tier.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {tier.popular && (
                    <Badge className="absolute -top-2 right-4 bg-purple-500 text-white">Most Popular</Badge>
                  )}
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{tier.name}</h3>
                        {selectedTier === tier.id && <Check className="w-5 h-5 text-purple-500" />}
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{tier.desc}</p>
                      <ul className="space-y-1">
                        {tier.features.slice(0, 3).map((f, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                            <Check className="w-3 h-3 text-green-500" />
                            {f}
                          </li>
                        ))}
                        {tier.features.length > 3 && (
                          <li className="text-sm text-gray-500">+{tier.features.length - 3} more</li>
                        )}
                      </ul>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">{tier.price}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <Zap className="w-5 h-5 text-green-600 mx-auto mb-2" />
              <p className="text-green-800 font-medium">14-day free trial included</p>
              <p className="text-green-600 text-sm">No credit card required to start</p>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {loading ? "Setting up..." : "Start Free Trial"}
                <Crown className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ProviderSetupPage;
